import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  CalendarMonth as CalendarIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { validationAPI, adminGroupsAPI } from '../services/api';
import { isHoliday, isItalianHoliday } from '../utils/holidays';
// @ts-ignore - xlsx-js-style non ha tipi TypeScript ufficiali
import * as XLSXStyle from 'xlsx-js-style';

interface DailyValidationDetail {
  giorno: number;
  ore_presenza: number;
  ore_ferie: number;
  ore_permesso: number;
  altre_assenze: number;
  motivazione_assenza?: string | null;
}

interface ValidationPreviewResponse {
  employee_id: number;
  employee_name: string;
  employee_surname: string;
  employee_matricola?: string | null;
  employee_sede?: string | null;
  year: number;
  month: number;
  month_name: string;
  total_presence_hours: number;
  total_leave_hours: number;
  total_permission_hours: number;
  total_other_absence_hours: number;
  total_expected_hours: number;
  total_working_days: number;
  daily_details: DailyValidationDetail[];
  can_validate: boolean;
  required_level?: string; // employee, group_admin, super_admin
  has_anomalies?: boolean;
  anomalies_count?: number;
  significant_anomalies_count?: number;
  minor_anomalies_count?: number;
  admin_can_validate_with_minor?: boolean;
  is_provisional: boolean;
  validation_message: string;
  already_validated: boolean;
  existing_validation?: PresenceValidation;
}

interface PresenceValidation {
  id: number;
  employee_id: number;
  user_id?: number; // Aggiunto per navigazione al cartellino
  employee_name?: string;
  employee_surname?: string;
  employee_matricola?: string | null;
  employee_sede?: string | null;
  year: number;
  month: number;
  total_presence_hours: number;
  total_leave_hours: number;
  total_permission_hours: number;
  total_other_absence_hours: number;
  total_working_days: number;
  validated_at: string;
  validated_by: number;
  validator_name: string;
  validator_surname: string;
  validation_notes?: string | null;
  is_provisional?: boolean;
  validation_level?: string; // employee, group_admin, super_admin
  has_anomalies?: boolean;
  group_id?: number | null; // ID del gruppo admin di appartenenza
  group_name?: string | null; // Nome del gruppo admin di appartenenza
}

const monthNames = [
  '', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
];

const PresenceValidation: React.FC = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = user?.role === 'admin';
  
  // Leggi i parametri URL per inizializzare year, month, user_id
  const urlYear = searchParams.get('year');
  const urlMonth = searchParams.get('month');
  const urlUserId = searchParams.get('user_id');
  
  const [year, setYear] = useState<number>(
    urlYear ? parseInt(urlYear) : new Date().getFullYear()
  );
  const [month, setMonth] = useState<number>(
    urlMonth ? parseInt(urlMonth) : new Date().getMonth() + 1
  );
  const [selectedUserId, setSelectedUserId] = useState<number | null>(
    urlUserId ? parseInt(urlUserId) : null
  );
  
  const [preview, setPreview] = useState<ValidationPreviewResponse | null>(null);
  const [allValidations, setAllValidations] = useState<PresenceValidation[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingValidations, setLoadingValidations] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [validationNotes, setValidationNotes] = useState('');
  const [hasAnomaliesFilter, setHasAnomaliesFilter] = useState<boolean | null>(null);
  const [selectedGroupFilter, setSelectedGroupFilter] = useState<number | null>(null);
  const [groups, setGroups] = useState<Array<{id: number, name: string, employee_ids?: number[]}>>([]);
  const [viewMode, setViewMode] = useState<'list' | 'byGroup'>('list');
  const [selectedEmployeeForValidation, setSelectedEmployeeForValidation] = useState<{userId: number, year: number, month: number} | null>(null);
  const [canExportTimesheet, setCanExportTimesheet] = useState(false);
  const [loadingExportPolicy, setLoadingExportPolicy] = useState(true);

  // Aggiorna i parametri URL quando cambiano year, month, selectedUserId
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedUserId) {
      params.set('user_id', selectedUserId.toString());
    }
    if (year) {
      params.set('year', year.toString());
    }
    if (month) {
      params.set('month', month.toString());
    }
    setSearchParams(params, { replace: true });
  }, [selectedUserId, year, month, setSearchParams]);

  useEffect(() => {
    // Se c'è user_id nei parametri URL o nello state, carica preview per quell'utente
    const userIdToLoad = selectedUserId || (urlUserId ? parseInt(urlUserId) : null);
    
    if (userIdToLoad && !isNaN(userIdToLoad)) {
      setSelectedUserId(userIdToLoad);
      loadPreview(userIdToLoad);
      return;
    }

    if (isAdmin) {
      loadGroups();
      loadAllValidations();
    } else {
      // Per i dipendenti, carica sempre la preview
      loadPreview();
    }
  }, [year, month, isAdmin, hasAnomaliesFilter, selectedGroupFilter]);

  useEffect(() => {
    let cancelled = false;

    const loadExportPolicy = async () => {
      if (!user) {
        if (!cancelled) {
          setCanExportTimesheet(false);
          setLoadingExportPolicy(false);
        }
        return;
      }

      try {
        if (!cancelled) setLoadingExportPolicy(true);
        const response = await adminGroupsAPI.getTimesheetExportPolicy();
        if (!cancelled) {
          setCanExportTimesheet(response.allowed !== false);
        }
      } catch {
        if (!cancelled) {
          setCanExportTimesheet(false);
        }
      } finally {
        if (!cancelled) {
          setLoadingExportPolicy(false);
        }
      }
    };

    loadExportPolicy();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const loadGroups = async () => {
    try {
      const data = user?.is_super_admin 
        ? await adminGroupsAPI.getAllGroups()
        : await adminGroupsAPI.getMyGroups();
      const groupsList = data.groups || [];
      
      // Carica dipendenti per ogni gruppo
      const groupsWithEmployees = await Promise.all(
        groupsList.map(async (group: any) => {
          try {
            const employeesData = user?.is_super_admin
              ? await adminGroupsAPI.getGroupEmployees(group.id)
              : await adminGroupsAPI.getMyGroupEmployees(group.id);
            const employeeIds = (employeesData.employees || []).map((e: any) => e.id || e.employee_id);
            return { ...group, employee_ids: employeeIds };
          } catch (err) {
            return { ...group, employee_ids: [] };
          }
        })
      );
      
      setGroups(groupsWithEmployees);
    } catch (err) {
      // Errore caricamento gruppi
    }
  };

  const loadAllValidations = async () => {
    setLoadingValidations(true);
    setError(null);
    try {
      const filters: any = {
        year,
        month,
        has_anomalies: hasAnomaliesFilter !== null ? hasAnomaliesFilter : undefined,
      };
      
      // Se è super admin e ha selezionato un gruppo specifico, passa il group_id
      // Se è super admin e non ha selezionato un gruppo, non passa group_id (vede tutto)
      // Se non è super admin, il backend filtra automaticamente per i suoi gruppi
      if (user?.is_super_admin && selectedGroupFilter !== null) {
        filters.group_id = selectedGroupFilter;
      }
      
      const data = await validationAPI.getValidationsForAdmin(filters);
      setAllValidations(data.validations || []);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore sconosciuto');
    } finally {
      setLoadingValidations(false);
    }
  };

  const loadPreview = async (targetUserId?: number) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const userId = targetUserId || (isAdmin ? undefined : undefined);
      const data = await validationAPI.getValidationPreview(year, month, userId);
      setPreview(data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('Dipendente non trovato. Assicurati di avere un profilo dipendente associato al tuo account.');
      } else {
        setError(err.response?.data?.error || err.message || 'Errore sconosciuto');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    if (!preview) return;

    setLoading(true);
    setError(null);
    try {
      await validationAPI.validatePresence({
        year,
        month,
        validation_notes: validationNotes || undefined,
      });

      setSuccess('Cartellino validato con successo!');
      setValidationDialogOpen(false);
      setValidationNotes('');
      loadPreview();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadINPAS = () => {
    if (!preview) return;
    if (!canExportTimesheet) {
      setError('Esportazione cartellino non consentita dalla policy attiva');
      return;
    }

    const wb = XLSXStyle.utils.book_new();
    const daysInMonth = new Date(year, month, 0).getDate();
    const inpasData: any[][] = [];

    // Header
    const employeeName = `${preview.employee_surname || ''} ${preview.employee_name || ''}`.trim().toUpperCase();
    const sede = preview.employee_sede || '';
    const fullName = sede ? `${employeeName} - ${sede}` : employeeName;
    const inpasMonthNames = [
      'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
      'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
    ];

    inpasData.push(['inpas', '', '', '', '', '', '']);
    inpasData.push(['Mese', inpasMonthNames[month - 1], '', 'Anno', year.toString(), '', '']);
    inpasData.push(['Nominativo e Sede', fullName, '', '', '', '', '']);
    inpasData.push([]);

    // Header tabella
    inpasData.push(['giorno', 'ore presenza', 'ore ferie', 'ore permesso', 'altre assenze', '', '']);
    inpasData.push(['', '', '', '', 'n.ore', 'motivazione', '']);

    // Dati giornalieri
    for (let day = 1; day <= daysInMonth; day++) {
      const detail = preview.daily_details?.find(d => d.giorno === day);

      let orePresenza = '';
      let oreFerie = '';
      let orePermesso = '';
      let altreAssenzeOre = '';
      let altreAssenzeMotivazione = '';

      if (detail) {
        if (detail.ore_presenza > 0) {
          orePresenza = Math.round(detail.ore_presenza).toString();
        }
        if (detail.ore_ferie > 0) {
          oreFerie = Math.round(detail.ore_ferie).toString();
        }
        if (detail.ore_permesso > 0) {
          orePermesso = Math.round(detail.ore_permesso).toString();
        }
        if (detail.altre_assenze > 0) {
          altreAssenzeOre = Math.round(detail.altre_assenze).toString();
          altreAssenzeMotivazione = detail.motivazione_assenza || 'ALTRE ASSENZE';
        }
      }

      inpasData.push([
        day.toString(),
        orePresenza,
        oreFerie,
        orePermesso,
        altreAssenzeOre,
        altreAssenzeMotivazione,
        '',
      ]);
    }

    // Footer
    inpasData.push([]);
    inpasData.push(['Ai fini della validità del documento è necessario che siano presenti le firme richieste in calce.', '', '', '', '', '', '']);
    inpasData.push([]);
    inpasData.push(['Firma Operatore', '', '', '', 'note', '', '']);
    inpasData.push(['', '', '', '', '', '', '']);
    inpasData.push(['', '', '', '', '', '', '']);

    // Crea foglio
    const ws = XLSXStyle.utils.aoa_to_sheet(inpasData);

    // Stili
    const lightBlueFill = { fgColor: { rgb: 'B3D9FF' } };
    const greyFill = { fgColor: { rgb: 'D3D3D3' } };

    const headerStyle = {
      fill: lightBlueFill,
      font: { bold: true, color: { rgb: '000000' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } },
      },
    };

    const defaultStyle = {
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } },
      },
    };

    const range = XLSXStyle.utils.decode_range(ws['!ref'] || 'A1');

    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cellAddress = XLSXStyle.utils.encode_cell({ r: R, c: C });
        if (!ws[cellAddress]) {
          ws[cellAddress] = { v: '', t: 's' };
        }

        if (R === 0 && C === 0) {
          ws[cellAddress].s = {
            ...headerStyle,
            font: { bold: true, size: 14, color: { rgb: '000080' } },
          };
        } else if (R === 1 && (C === 0 || C === 1 || C === 3 || C === 4)) {
          ws[cellAddress].s = headerStyle;
        } else if (R === 2 && (C === 0 || C === 1)) {
          ws[cellAddress].s = headerStyle;
        } else if ((R === 4 || R === 5) && C <= 5) {
          ws[cellAddress].s = headerStyle;
        } else if (R >= 6 && R < 6 + daysInMonth && C === 0) {
          const dayNum = R - 5;
          const date = new Date(year, month - 1, dayNum);
          const isWeekend = isItalianHoliday(date);
          ws[cellAddress].s = {
            ...defaultStyle,
            fill: isWeekend ? greyFill : undefined,
          };
        } else if (R >= 6 && R < 6 + daysInMonth && C <= 6) {
          ws[cellAddress].s = defaultStyle;
        } else if (R === range.e.r - 2 && (C === 0 || C === 4)) {
          ws[cellAddress].s = headerStyle;
        } else {
          ws[cellAddress].s = defaultStyle;
        }
      }
    }

    // Larghezza colonne
    ws['!cols'] = [
      { wch: 8 },  // giorno
      { wch: 12 }, // ore presenza
      { wch: 10 }, // ore ferie
      { wch: 25 }, // ore permesso
      { wch: 10 }, // n.ore (altre assenze)
      { wch: 25 }, // motivazione
      { wch: 10 }, // colonna vuota
    ];

    // Altezza righe
    ws['!rows'] = [];
    for (let R = 0; R <= range.e.r; R++) {
      ws['!rows'][R] = { hpt: 20 };
      if (R === 4 || R === 5) {
        ws['!rows'][R] = { hpt: 25 };
      }
    }

    // Unisci celle per "altre assenze"
    if (!ws['!merges']) ws['!merges'] = [];
    ws['!merges'].push({ s: { r: 4, c: 4 }, e: { r: 4, c: 5 } });

    XLSXStyle.utils.book_append_sheet(wb, ws, 'Cartellino');

    // Nome file
    const cleanFileName = (str: string) => str.replace(/[\\\/\?\*\[\]:<>|"]/g, '_').trim();
    const cognome = cleanFileName((preview.employee_surname || '').trim());
    const nome = cleanFileName((preview.employee_name || '').trim());
    const filename = `Cartellino_INPAS_${cognome}_${nome}_${inpasMonthNames[month - 1]}_${year}.xlsx`;

    // Download
    const wbout = XLSXStyle.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const getDayName = (day: number, month: number, year: number): string => {
    const date = new Date(year, month - 1, day);
    const days = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
    return days[date.getDay()];
  };

  const isWeekend = (day: number, month: number, year: number): boolean => {
    return isHoliday(day, month, year);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" component="h1">
              Validazione Presenze Mensili
            </Typography>
            {preview && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                  {preview.employee_name} {preview.employee_surname}
                  {preview.employee_matricola && ` - Matr. ${preview.employee_matricola}`}
                  {preview.employee_sede && ` - ${preview.employee_sede}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {preview.month_name} {preview.year}
                </Typography>
              </Box>
            )}
            {selectedUserId && isAdmin && (
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setSelectedUserId(null);
                  setPreview(null);
                  const params = new URLSearchParams();
                  params.set('year', year.toString());
                  params.set('month', month.toString());
                  setSearchParams(params, { replace: true });
                  loadAllValidations();
                }}
                sx={{ mt: 1 }}
              >
                ← Torna alla lista
              </Button>
            )}
          </Box>
          <Box>
            {(!isAdmin || preview) && (
              <Button
                variant="contained"
                color={
                  preview?.has_anomalies && (preview?.significant_anomalies_count ?? 0) > 0 ? "error" 
                  : preview?.admin_can_validate_with_minor ? "warning"
                  : "primary"
                }
                startIcon={<CheckCircleIcon />}
                onClick={() => {
                  if (!preview) {
                    loadPreview();
                  } else {
                    setValidationDialogOpen(true);
                  }
                }}
                disabled={
                  loading || 
                  (preview && (!preview.can_validate || preview.already_validated)) ||
                  (preview && (preview.significant_anomalies_count ?? 0) > 0) ||
                  (preview && preview.admin_can_validate_with_minor && !isAdmin)
                }
                sx={{ mr: 1 }}
              >
                {preview?.already_validated 
                  ? "Già Validato"
                  : !preview?.can_validate && preview
                    ? preview.validation_message || "Non validabile"
                  : (preview?.significant_anomalies_count ?? 0) > 0
                    ? `Anomalie da correggere (${preview?.significant_anomalies_count})`
                    : preview?.admin_can_validate_with_minor && isAdmin
                      ? `Valida con ${preview?.minor_anomalies_count} anomalie minori`
                      : preview?.admin_can_validate_with_minor && !isAdmin
                        ? "Richiede Admin per anomalie minori"
                        : preview 
                          ? "Valida Cartellino" 
                          : "Carica Preview"}
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{ mr: 1 }}
            >
              Stampa
            </Button>
            {preview && canExportTimesheet && !loadingExportPolicy && (
              <Button
                variant="outlined"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadINPAS}
                sx={{ mr: 1 }}
              >
                Esporta Cartellino
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<CalendarIcon />}
              onClick={() => {
                if (selectedUserId) {
                  // Se c'è un utente selezionato, carica la preview
                  loadPreview(selectedUserId);
                } else if (isAdmin) {
                  // Altrimenti, se è admin, carica la lista
                  loadAllValidations();
                } else {
                  // Per i dipendenti, carica sempre la preview
                  loadPreview();
                }
              }}
            >
              Aggiorna
            </Button>
          </Box>
        </Box>

        {/* Selezione Anno/Mese */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Anno</InputLabel>
              <Select
                value={year}
                label="Anno"
                onChange={(e: SelectChangeEvent<number>) => {
                  setYear(e.target.value as number);
                  // Ricarica automaticamente la preview quando cambia l'anno
                  if (!isAdmin) {
                    setTimeout(() => loadPreview(), 100);
                  }
                }}
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Mese</InputLabel>
              <Select
                value={month}
                label="Mese"
                onChange={(e: SelectChangeEvent<number>) => {
                  const newMonth = e.target.value as number;
                  setMonth(newMonth);
                  // Aggiorna l'URL
                  const params = new URLSearchParams(searchParams);
                  params.set('month', newMonth.toString());
                  setSearchParams(params, { replace: true });
                  // Ricarica automaticamente la preview quando cambia il mese
                  if (selectedUserId) {
                    setTimeout(() => loadPreview(selectedUserId), 100);
                  } else if (!isAdmin) {
                    setTimeout(() => loadPreview(), 100);
                  }
                }}
              >
                {monthNames.slice(1).map((name, index) => (
                  <MenuItem key={index + 1} value={index + 1}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Alert anomalie significative (bloccanti) */}
        {preview && (preview.significant_anomalies_count ?? 0) > 0 && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }} 
            icon={<WarningIcon />}
            action={
              isAdmin && selectedUserId ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    navigate(`/timesheet?user_id=${selectedUserId}&year=${year}&month=${month}`);
                  }}
                >
                  Vai al Cartellino
                </Button>
              ) : null
            }
          >
            <Typography variant="body1" fontWeight="bold" gutterBottom>
              Il cartellino presenta {preview.significant_anomalies_count} anomalie significative
            </Typography>
            <Typography variant="body2">
              Queste anomalie superano la soglia di tolleranza (15 min) e devono essere corrette prima della validazione.
            </Typography>
          </Alert>
        )}

        {/* Alert anomalie minori (non bloccanti per admin) */}
        {preview && preview.admin_can_validate_with_minor && (preview.minor_anomalies_count ?? 0) > 0 && (
          <Alert 
            severity="warning" 
            sx={{ mb: 2 }} 
            icon={<WarningIcon />}
          >
            <Typography variant="body1" fontWeight="bold" gutterBottom>
              Il cartellino presenta {preview.minor_anomalies_count} anomalie minori (sotto 15 min)
            </Typography>
            <Typography variant="body2">
              {isAdmin 
                ? 'Le anomalie sono sotto la soglia di tolleranza. Puoi validare direttamente il cartellino.'
                : 'Le anomalie sono sotto la soglia di tolleranza. Un amministratore può validare direttamente il cartellino.'}
            </Typography>
          </Alert>
        )}

        {isAdmin && !selectedUserId ? (
          // Vista Admin: mostra tutte le validazioni (solo se non c'è un utente selezionato)
          <>
            {loadingValidations ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
                  <Typography variant="h5">
                    Validazioni Presenze - {monthNames[month]} {year}
                  </Typography>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Vista</InputLabel>
                      <Select
                        value={viewMode}
                        label="Vista"
                        onChange={(e) => setViewMode(e.target.value as 'list' | 'byGroup')}
                      >
                        <MenuItem value="list">Lista</MenuItem>
                        <MenuItem value="byGroup">Per Gruppo</MenuItem>
                      </Select>
                    </FormControl>
                    {viewMode === 'list' && (
                      <>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <InputLabel>Filtra Gruppo</InputLabel>
                          <Select
                            value={selectedGroupFilter || 'all'}
                            label="Filtra Gruppo"
                            onChange={(e) => {
                              const value = e.target.value;
                              setSelectedGroupFilter(value === 'all' ? null : parseInt(String(value)));
                            }}
                          >
                            <MenuItem value="all">Tutti i Gruppi</MenuItem>
                            {groups.map((g) => (
                              <MenuItem key={g.id} value={g.id.toString()}>
                                {g.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <InputLabel>Filtra Anomalie</InputLabel>
                          <Select
                            value={hasAnomaliesFilter === null ? 'all' : hasAnomaliesFilter ? 'yes' : 'no'}
                            label="Filtra Anomalie"
                            onChange={(e) => {
                              const value = e.target.value;
                              setHasAnomaliesFilter(value === 'all' ? null : value === 'yes');
                            }}
                          >
                            <MenuItem value="all">Tutte</MenuItem>
                            <MenuItem value="yes">Con Anomalie</MenuItem>
                            <MenuItem value="no">Senza Anomalie</MenuItem>
                          </Select>
                        </FormControl>
                      </>
                    )}
                  </Box>
                </Box>
                {allValidations.length === 0 ? (
                  <Typography variant="body1" color="text.secondary" align="center" p={4}>
                    Nessuna validazione trovata per il periodo selezionato
                  </Typography>
                ) : viewMode === 'byGroup' ? (
                  // Vista organizzata per gruppi
                  <Box>
                    {groups.map((group) => {
                      // Filtra validazioni per questo gruppo
                      const groupValidations = allValidations.filter(v => 
                        group.employee_ids?.includes(v.employee_id)
                      );
                      
                      if (groupValidations.length === 0) return null;
                      
                      return (
                        <Accordion key={group.id} sx={{ mb: 2 }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" pr={2}>
                              <Typography variant="h6">{group.name}</Typography>
                              <Chip 
                                label={`${groupValidations.length} validazione/i`} 
                                color="primary" 
                                size="small"
                                sx={{ ml: 2 }}
                              />
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <TableContainer component={Paper}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Dipendente</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ore Presenza</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ore Ferie</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ore Permesso</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Anomalie</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Livello</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Validato da</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Azioni</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {groupValidations.map((validation) => (
                                    <TableRow key={validation.id}>
                                      <TableCell>
                                        {validation.employee_name} {validation.employee_surname}
                                        {validation.employee_matricola && (
                                          <Typography variant="caption" display="block" color="text.secondary">
                                            Matr: {validation.employee_matricola}
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell align="center">
                                        {validation.total_presence_hours.toFixed(2)}h
                                      </TableCell>
                                      <TableCell align="center">
                                        {validation.total_leave_hours.toFixed(2)}h
                                      </TableCell>
                                      <TableCell align="center">
                                        {validation.total_permission_hours.toFixed(2)}h
                                      </TableCell>
                                      <TableCell align="center">
                                        {validation.has_anomalies ? (
                                          <Chip label="⚠️ Sì" color="error" size="small" />
                                        ) : (
                                          <Chip label="✅ No" color="success" size="small" />
                                        )}
                                      </TableCell>
                                      <TableCell align="center">
                                        {validation.validation_level === 'employee' ? (
                                          <Chip label="Dipendente" color="default" size="small" />
                                        ) : validation.validation_level === 'group_admin' ? (
                                          <Chip label="Admin Gruppo" color="primary" size="small" />
                                        ) : (
                                          <Chip label="Super Admin" color="secondary" size="small" />
                                        )}
                                      </TableCell>
                                      <TableCell align="center">
                                        {validation.validator_name} {validation.validator_surname}
                                      </TableCell>
                                      <TableCell align="center">
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          onClick={async () => {
                                            try {
                                              // Imposta l'utente selezionato e aggiorna l'URL
                                              setSelectedUserId(validation.user_id);
                                              setYear(validation.year);
                                              setMonth(validation.month);
                                              
                                              const params = new URLSearchParams();
                                              params.set('user_id', validation.user_id.toString());
                                              params.set('year', validation.year.toString());
                                              params.set('month', validation.month.toString());
                                              setSearchParams(params, { replace: true });
                                              
                                              const previewData = await validationAPI.getValidationPreview(
                                                validation.year,
                                                validation.month,
                                                validation.user_id
                                              );
                                              setPreview(previewData);
                                              window.scrollTo({ top: 0, behavior: 'smooth' });
                                            } catch (err: any) {
                                              setError(err.response?.data?.error || 'Errore nel caricamento preview');
                                            }
                                          }}
                                        >
                                          Visualizza
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                    {/* Sezione per dipendenti senza gruppo */}
                    {(() => {
                      // Trova tutte le validazioni che non appartengono a nessun gruppo
                      const allGroupEmployeeIds = new Set(
                        groups.flatMap(g => g.employee_ids || [])
                      );
                      const validationsWithoutGroup = allValidations.filter(v => 
                        !allGroupEmployeeIds.has(v.employee_id) && (!v.group_id || v.group_id === null)
                      );
                      
                      if (validationsWithoutGroup.length === 0) return null;
                      
                      return (
                        <Accordion sx={{ mb: 2 }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" pr={2}>
                              <Typography variant="h6">Senza Gruppo</Typography>
                              <Chip 
                                label={`${validationsWithoutGroup.length} validazione/i`} 
                                color="default" 
                                size="small"
                                sx={{ ml: 2 }}
                              />
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <TableContainer component={Paper}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Dipendente</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ore Presenza</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ore Ferie</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ore Permesso</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Anomalie</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Livello</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Validato da</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Azioni</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {validationsWithoutGroup.map((validation) => (
                                    <TableRow key={validation.id}>
                                      <TableCell>
                                        {validation.employee_name} {validation.employee_surname}
                                        {validation.employee_matricola && (
                                          <Typography variant="caption" display="block" color="text.secondary">
                                            Matr: {validation.employee_matricola}
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell align="center">
                                        {validation.total_presence_hours.toFixed(2)}h
                                      </TableCell>
                                      <TableCell align="center">
                                        {validation.total_leave_hours.toFixed(2)}h
                                      </TableCell>
                                      <TableCell align="center">
                                        {validation.total_permission_hours.toFixed(2)}h
                                      </TableCell>
                                      <TableCell align="center">
                                        {validation.has_anomalies ? (
                                          <Chip label="⚠️ Sì" color="error" size="small" />
                                        ) : (
                                          <Chip label="✅ No" color="success" size="small" />
                                        )}
                                      </TableCell>
                                      <TableCell align="center">
                                        {validation.validation_level === 'employee' ? (
                                          <Chip label="Dipendente" color="default" size="small" />
                                        ) : validation.validation_level === 'group_admin' ? (
                                          <Chip label="Admin Gruppo" color="primary" size="small" />
                                        ) : (
                                          <Chip label="Super Admin" color="secondary" size="small" />
                                        )}
                                      </TableCell>
                                      <TableCell align="center">
                                        {validation.validator_name} {validation.validator_surname}
                                      </TableCell>
                                      <TableCell align="center">
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          onClick={async () => {
                                            try {
                                              // Imposta l'utente selezionato e aggiorna l'URL
                                              setSelectedUserId(validation.user_id);
                                              setYear(validation.year);
                                              setMonth(validation.month);
                                              
                                              const params = new URLSearchParams();
                                              params.set('user_id', validation.user_id.toString());
                                              params.set('year', validation.year.toString());
                                              params.set('month', validation.month.toString());
                                              setSearchParams(params, { replace: true });
                                              
                                              const previewData = await validationAPI.getValidationPreview(
                                                validation.year,
                                                validation.month,
                                                validation.user_id
                                              );
                                              setPreview(previewData);
                                              window.scrollTo({ top: 0, behavior: 'smooth' });
                                            } catch (err: any) {
                                              setError(err.response?.data?.error || 'Errore nel caricamento preview');
                                            }
                                          }}
                                        >
                                          Visualizza
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })()}
                    {groups.filter(g => allValidations.some(v => g.employee_ids?.includes(v.employee_id))).length === 0 && 
                     allValidations.filter(v => !groups.some(g => g.employee_ids?.includes(v.employee_id)) && (!v.group_id || v.group_id === null)).length === 0 && (
                      <Typography variant="body1" color="text.secondary" align="center" p={4}>
                        Nessuna validazione trovata per i gruppi disponibili
                      </Typography>
                    )}
                  </Box>
                ) : (
                  // Vista lista normale
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Dipendente</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Mese</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ore Presenza</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ore Ferie</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ore Permesso</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Altre Assenze</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Giorni Lavorativi</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Anomalie</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Livello</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Stato</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Validato da</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Data Validazione</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allValidations.map((validation) => (
                          <TableRow key={validation.id}>
                            <TableCell>
                              {validation.employee_name} {validation.employee_surname}
                              {validation.employee_matricola && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  Matr: {validation.employee_matricola}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {monthNames[validation.month]} {validation.year}
                            </TableCell>
                            <TableCell align="center">
                              {validation.total_presence_hours.toFixed(2)}h
                            </TableCell>
                            <TableCell align="center">
                              {validation.total_leave_hours.toFixed(2)}h
                            </TableCell>
                            <TableCell align="center">
                              {validation.total_permission_hours.toFixed(2)}h
                            </TableCell>
                            <TableCell align="center">
                              {validation.total_other_absence_hours.toFixed(2)}h
                            </TableCell>
                            <TableCell align="center">
                              {validation.total_working_days}
                            </TableCell>
                            <TableCell align="center">
                              {validation.has_anomalies ? (
                                <Chip label="⚠️ Sì" color="error" size="small" />
                              ) : (
                                <Chip label="✅ No" color="success" size="small" />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {validation.validation_level === 'employee' ? (
                                <Chip label="Dipendente" color="default" size="small" />
                              ) : validation.validation_level === 'group_admin' ? (
                                <Chip label="Admin Gruppo" color="primary" size="small" />
                              ) : (
                                <Chip label="Super Admin" color="secondary" size="small" />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Chip label="Validato" color="success" size="small" />
                            </TableCell>
                            <TableCell align="center">
                              {validation.validator_name} {validation.validator_surname}
                            </TableCell>
                            <TableCell align="center">
                              {new Date(validation.validated_at).toLocaleDateString('it-IT', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={async () => {
                                  // Imposta l'utente selezionato e aggiorna l'URL
                                  setSelectedUserId(validation.user_id);
                                  setYear(validation.year);
                                  setMonth(validation.month);
                                  
                                  const params = new URLSearchParams();
                                  params.set('user_id', validation.user_id.toString());
                                  params.set('year', validation.year.toString());
                                  params.set('month', validation.month.toString());
                                  setSearchParams(params, { replace: true });
                                  
                                  // Carica preview per questo dipendente nella stessa pagina
                                  try {
                                    const previewData = await validationAPI.getValidationPreview(
                                      validation.year,
                                      validation.month,
                                      validation.user_id
                                    );
                                    setPreview(previewData);
                                    // Scrolla alla preview
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                  } catch (err: any) {
                                    setError(err.response?.data?.error || 'Errore nel caricamento preview');
                                  }
                                }}
                              >
                                Visualizza
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </>
        ) : loading && !preview ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : preview ? (
          <>
            {/* Header con informazioni dipendente */}
            <Box mb={3} p={2} bgcolor="primary.main" color="primary.contrastText" borderRadius={1}>
              <Typography variant="h5" gutterBottom>
                {preview.employee_name} {preview.employee_surname}
                {preview.employee_matricola && ` - Matricola: ${preview.employee_matricola}`}
              </Typography>
              <Typography variant="body1">
                {preview.employee_sede && `Sede: ${preview.employee_sede}`}
              </Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                Mese: {preview.month_name} {preview.year}
              </Typography>
            </Box>

            {/* Tabella dettaglio giornaliero */}
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Giorno</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ore Presenza</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ore Ferie</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ore Permesso</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Altre Assenze</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Motivazione</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.daily_details.map((detail) => {
                    const isWeekendDay = isWeekend(detail.giorno, preview.month, preview.year);
                    return (
                      <TableRow
                        key={detail.giorno}
                        sx={{
                          bgcolor: isWeekendDay ? 'action.hover' : 'inherit',
                        }}
                      >
                        <TableCell align="center">
                          {detail.giorno}
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {getDayName(detail.giorno, preview.month, preview.year)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {detail.ore_presenza > 0 ? detail.ore_presenza.toFixed(2) : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {detail.ore_ferie > 0 ? detail.ore_ferie.toFixed(2) : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {detail.ore_permesso > 0 ? detail.ore_permesso.toFixed(2) : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {detail.altre_assenze > 0 ? detail.altre_assenze.toFixed(2) : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {detail.motivazione_assenza || '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Riepilogo totale */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Riepilogo Mensile
                  </Typography>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>Totale Ore Presenza:</Typography>
                    <Typography fontWeight="bold">
                      {preview.total_presence_hours.toFixed(2)}h
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>Totale Ore Ferie:</Typography>
                    <Typography fontWeight="bold">
                      {preview.total_leave_hours.toFixed(2)}h
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>Totale Ore Permesso:</Typography>
                    <Typography fontWeight="bold">
                      {preview.total_permission_hours.toFixed(2)}h
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>Totale Altre Assenze:</Typography>
                    <Typography fontWeight="bold">
                      {preview.total_other_absence_hours.toFixed(2)}h
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>Giorni Lavorativi:</Typography>
                    <Typography fontWeight="bold">
                      {preview.total_working_days}
                    </Typography>
                  </Box>
                  {preview.total_expected_hours != null && (
                    <>
                      <Box display="flex" justifyContent="space-between" mb={1} sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography fontWeight="bold">Ore Attese Totali:</Typography>
                        <Typography fontWeight="bold">
                          {preview.total_expected_hours.toFixed(2)}h
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography fontWeight="bold">Ore Effettive Totali:</Typography>
                        <Typography fontWeight="bold">
                          {(preview.total_presence_hours + preview.total_permission_hours + preview.total_leave_hours + preview.total_other_absence_hours).toFixed(2)}h
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography fontWeight="bold" color={preview.total_expected_hours - (preview.total_presence_hours + preview.total_permission_hours + preview.total_leave_hours + preview.total_other_absence_hours) > 0.01 ? 'error.main' : 'success.main'}>
                          Differenza:
                        </Typography>
                        <Typography fontWeight="bold" color={preview.total_expected_hours - (preview.total_presence_hours + preview.total_permission_hours + preview.total_leave_hours + preview.total_other_absence_hours) > 0.01 ? 'error.main' : 'success.main'}>
                          {(preview.total_expected_hours - (preview.total_presence_hours + preview.total_permission_hours + preview.total_leave_hours + preview.total_other_absence_hours)).toFixed(2)}h
                        </Typography>
                      </Box>
                    </>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Stato Validazione
                  </Typography>
                  {preview.already_validated && preview.existing_validation ? (
                    <>
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Validato"
                        color="success"
                        sx={{ mb: 2 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Validato il:{' '}
                        {new Date(preview.existing_validation.validated_at).toLocaleDateString('it-IT', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Da: {preview.existing_validation.validator_name}{' '}
                        {preview.existing_validation.validator_surname}
                      </Typography>
                      {preview.existing_validation.validation_notes && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Note: {preview.existing_validation.validation_notes}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <>
                      {preview.can_validate ? (
                        <>
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Pronto per la validazione"
                            color="success"
                            sx={{ mb: 2 }}
                          />
                          <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={() => setValidationDialogOpen(true)}
                            disabled={loading}
                          >
                            Valida Cartellino
                          </Button>
                        </>
                      ) : (
                        <>
                          <Chip
                            icon={<WarningIcon />}
                            label={
                              (preview.significant_anomalies_count ?? 0) > 0
                                ? "Anomalie da risolvere"
                                : preview.admin_can_validate_with_minor
                                  ? "Richiede validazione admin"
                                  : "Non ancora validabile"
                            }
                            color={
                              (preview.significant_anomalies_count ?? 0) > 0 ? "error" 
                              : preview.admin_can_validate_with_minor ? "warning"
                              : "default"
                            }
                            sx={{ mb: 2 }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {preview.validation_message}
                          </Typography>
                        </>
                      )}
                    </>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {/* Dialog Validazione */}
            <Dialog
              open={validationDialogOpen}
              onClose={() => setValidationDialogOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>
                Conferma Validazione Cartellino
              </DialogTitle>
              <DialogContent>
                {!preview ? (
                  <Alert severity="error">
                    Errore: Preview non disponibile. Carica prima la preview del cartellino.
                  </Alert>
                ) : (
                  <>
                    <Typography variant="body1" gutterBottom>
                      Stai per validare il cartellino di <strong>{preview.month_name} {preview.year}</strong>.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Una volta validato, per modificarlo sarà necessario revocare la validazione.
                    </Typography>
                  </>
                )}
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Note (opzionale)"
                  value={validationNotes}
                  onChange={(e) => setValidationNotes(e.target.value)}
                  placeholder="Inserisci eventuali note sulla validazione..."
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setValidationDialogOpen(false)}>
                  Annulla
                </Button>
                <Button
                  onClick={handleValidate}
                  variant="contained"
                  color="primary"
                  disabled={loading || !preview}
                >
                  {loading ? <CircularProgress size={24} /> : 'Conferma Validazione'}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        ) : (
          <Typography variant="body1" color="text.secondary" align="center" p={4}>
            Seleziona un anno e un mese per visualizzare il cartellino
          </Typography>
        )}
      </Paper>
    </Container>
  );
};

export default PresenceValidation;
